import { Button } from "components/ui/button";
import { useAtom, useAtomValue } from "lib/global-context";
import {
  currentStepAtom,
  userIdAtom,
  ageAtom,
  goalTimelineAtom,
  goalDescriptionAtom,
  goalAmountAtom,
} from "lib/atoms";
import Simulator from "../common/simulator";
import BackButton from "../common/back-button";
import InputAge from "./form-fields/input-age";
import GoalSelect from "./form-fields/goal-select";
import GoalSlider from "./form-fields/goal-slider";
import Results from "./results";
import InputTimeline from "./form-fields/input-timeline";
import { isValidAge, isValidTimeline, isValidGoalAmount } from "lib/validation";
import InputGoal from "./form-fields/input-goal";
import {
  AnswerData,
  finalizeQuestionnaire,
  submitQuestionnaireAnswer,
} from "lib/api";
import { cn, mapQuestionsById } from "lib/utils";
import Disclaimer from "../common/disclaimer";
import RestartButton from "../common/restart-button";
import { formatAnswers, trackSubmission } from "../common/GTMProvider";
import { generateLink, handleLinkClick } from "lib/links";
import { getGTMProps } from "types/common";
import styles from "./simulator-form.module.css";

interface SimulatorFormProps {
  questions: (
    | {
        questionType: "numeric" | string;
        questionId: string;
        min: number;
        max: number;
        displayConfiguration: {
          type: "text" | string;
          title: string;
          placeholder: string;
          name: string;
          description: string;
          errorLocation: string;
          inputType: "number" | string;
          minErrorText: string;
          maxErrorText: string;
          pageName: string;
          choices?: undefined;
        };
        options?: undefined;
      }
    | {
        questionType: "select" | string;
        questionId: string;
        options: string[];
        displayConfiguration: {
          type: "dropdown" | string;
          title: string;
          name: string;
          choices: {
            id: string;
            value: string;
            headline: string;
            minGoal: number;
            maxGoal: number;
            span: string;
            defaultGoal: number;
          }[];
          placeholder: string;
          description?: undefined;
          errorLocation?: undefined;
          inputType?: undefined;
          minErrorText?: undefined;
          maxErrorText?: undefined;
          pageName?: undefined;
        };
        min?: undefined;
        max?: undefined;
      }
  )[];
}

type Choice = NonNullable<
  SimulatorFormProps["questions"][number]["displayConfiguration"]["choices"]
>[number];

export function SimulatorForm({ questions }: SimulatorFormProps) {
  const questionMap = mapQuestionsById(questions);

  const [currentStep, setCurrentStep] = useAtom(currentStepAtom);
  const userId = useAtomValue(userIdAtom);
  const age = useAtomValue(ageAtom);
  const timeline = useAtomValue(goalTimelineAtom);
  const goal = useAtomValue(goalDescriptionAtom);
  const amount = useAtomValue(goalAmountAtom);

  const selectedGoalDetails = questionMap.q3.displayConfiguration.choices?.find(
    (choice: Choice) => choice.value === goal,
  );

  const gtmProps = getGTMProps("wealth");

  const isFormValid = (): boolean => {
    return (
      isValidAge(
        age,
        questionMap.q1.min || Number.MIN_SAFE_INTEGER,
        questionMap.q1.max || Number.MAX_SAFE_INTEGER,
      ) &&
      isValidTimeline(timeline, age) &&
      !!goal &&
      isValidGoalAmount(amount)
    );
  };

  const getAnswerData = (): AnswerData[] => [
    { questionId: questionMap.q1.questionId, value: [age ?? 0] },
    { questionId: questionMap.q2.questionId, value: [timeline ?? 0] },
    { questionId: questionMap.q3.questionId, value: [goal ?? ""] },
    { questionId: questionMap.q4.questionId, value: [amount ?? 0] },
  ];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isFormValid()) {
      alert("Please ensure all fields are filled out correctly.");
      return;
    }

    const answerData = getAnswerData();
    const p1 = submitQuestionnaireAnswer(userId, answerData[0], gtmProps);
    const p2 = submitQuestionnaireAnswer(userId, answerData[1], gtmProps);
    const p3 = submitQuestionnaireAnswer(userId, answerData[2], gtmProps);
    const p4 = submitQuestionnaireAnswer(userId, answerData[3], gtmProps);
    Promise.all([p1, p2, p3, p4]).then(() =>
      finalizeQuestionnaire(userId, gtmProps),
    );

    trackSubmission({
      formId: `gcb_${userId}`,
      ...gtmProps,
      formStep: currentStep,
      quizStep: 1,
      label: "calculate_now",
      answers: formatAnswers(answerData),
    });

    setCurrentStep(2);
  };

  return (
    <Simulator>
      <div className={styles.container}>
        <div
          className={cn(
            styles.innerContainer,
            currentStep === 1 && styles.step1Container,
            currentStep === 3 && styles.resultsContainer,
          )}
        >
          <div className={styles.stepIndicator}>
            <div className={styles.stepText}>Step {currentStep} of 3</div>
          </div>
          <div>
            {currentStep === 1 && (
              <h2 className={styles.heading}>
                Answer 3 questions and we&rsquo;ll map out your potential
                journey.
              </h2>
            )}
            {currentStep === 2 && (
              <h2 className={styles.heading}>
                See how starting today makes all the difference! Compare two
                paths—one where you act now and one with a delayed start.
              </h2>
            )}
            {currentStep === 3 && (
              <h2 className={styles.heading}>
                Ready to make your vision a reality?{" "}
                <br className={styles.break} />
                Take the next step with us and enjoy a S$20 reward!
              </h2>
            )}
          </div>

          {currentStep === 1 && (
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.grid}>
                <InputAge config={questionMap.q1} />
                <InputTimeline config={questionMap.q2} />
                <GoalSelect config={questionMap.q3} />
              </div>

              {goal === "others" && selectedGoalDetails && (
                <InputGoal
                  minGoal={selectedGoalDetails.minGoal}
                  maxGoal={selectedGoalDetails.maxGoal}
                  headline={selectedGoalDetails.headline}
                  defaultValue={selectedGoalDetails.defaultGoal}
                />
              )}

              {goal !== "others" && selectedGoalDetails && (
                <GoalSlider
                  minGoal={selectedGoalDetails.minGoal}
                  maxGoal={selectedGoalDetails.maxGoal}
                  defaultValue={selectedGoalDetails.defaultGoal}
                  headline={selectedGoalDetails.headline}
                  span={selectedGoalDetails.span}
                />
              )}

              <div className={styles.buttonContainer}>
                <Button type="submit" disabled={!isFormValid()}>
                  View your journey
                </Button>
              </div>
            </form>
          )}

          {currentStep === 2 && (
            <div>
              <Results
                formData={{
                  age,
                  timeline,
                  goal,
                  amount,
                }}
              />
            </div>
          )}

          {currentStep === 2 && (
            <div className={styles.buttonContainer}>
              <BackButton />
              <Button onClick={() => setCurrentStep((prev) => prev + 1)}>
                What&rsquo;s next?
              </Button>
            </div>
          )}

          {currentStep === 3 && (
            <div className={styles.finalButtonContainer}>
              <a
                onClick={(e) => handleLinkClick(e, "wealth", userId)}
                href={generateLink("contactus", "wealth")}
                target="_top"
                className={styles.link}
              >
                <Button>Talk to us now</Button>
              </a>
              <RestartButton />
            </div>
          )}

          <Disclaimer page="wealth" />
        </div>
      </div>
    </Simulator>
  );
}
